
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import Swal from 'sweetalert2'
import { Link, useLocation } from 'react-router-dom'

import { useAuth } from '../../Hooks/UseAuth'

import AuthLayout from '../../Layouts/AuthLayout/AuthLayout'

import Toast from '../../Components/Toast/Toast'

import SessionActions from '../../Redux/Actions/SessionActions'
import useTranslation from '../../Hooks/UseTranslation'
import { useNotifications } from '@/Hooks/UseNotifications'

import './LoginPage.scss'

const LoginPage = () => {

    const { t } = useTranslation()

    const { login, loginError } = useAuth()
    const { token } = useNotifications()

    const dispatch = useDispatch()
    const location = useLocation()


    const [ dispatching, setDispatching ] = useState(false)

    const [ username, setUsername ] = useState('')
    const [ usernameError, setUsernameError ] = useState('')

    const [ password, setPassword ] = useState('')
    const [ passwordError, setPasswordError ] = useState('')
    const [ showPassword, setShowPassword ] = useState(false)

    const [ swalShwon, setSwalShown ] = useState(false)

    const handleChange = (name: string, value: string) => {

        if (name === 'username') {

            setUsername(value)

            if (value === '') {

                setUsernameError(t('LOGIN:EnterUsername'))
            } else {

                setUsernameError('')
            }
        } else if (name === 'password') {

            setPassword(value)

            if (value === '') {

                setPasswordError(t('LOGIN:EnterPassword'))
            } else {

                setPasswordError('')
            }
        }
    }

    const validateForm = () => {

        let isCorrect: boolean = true

        if (username === '') {

            setUsernameError(t('LOGIN:EnterUsername'))
            isCorrect = false
        }

        if (password === '') {

            setPasswordError(t('LOGIN:EnterPassword'))
            isCorrect = false
        }

        return isCorrect
    }

    const handleSubmit = async () => {

        if (!validateForm()) {

            return
        }

        setDispatching(true)

        const loginData = {
            username,
            password
        }

        await login(loginData, token)

        setDispatching(false)
    }

    const confirmed = new URLSearchParams(location.search).get('confirmed')

    if (confirmed === '' && !swalShwon) {

        Swal.fire({
            title: t('LOGIN:UserConfirmedTitle'),
            text: t('LOGIN:UserConfirmedContent'),
            icon: 'success',
            confirmButtonText: '¡Cerrar!',
            willClose: () => setSwalShown(true)
        })
    }

    useEffect(() => {

        const evalLoginError = () => {

            let toastTitle: string = ''
            let toastContent: string = ''
            let showToast: boolean = false

            if (loginError === 'not_verified') {

                Swal.fire({
                    title: t('LOGIN:UserConfirmedTitle'),
                    text: t('LOGIN:UserConfirmedContent'),
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: t('LOGIN:ResendEmail'),
                    cancelButtonText: t('LOGIN:Close'),
                    willClose: () => setSwalShown(true)
                }).then((result) => {
                    if (result.isConfirmed) {

                        const reConfirmData = {
                            username,
                        }

                        dispatch(SessionActions.reConfirmAction(reConfirmData))
                        Toast.show(
                            t('LOGIN:MailResentTitle'),
                            t('LOGIN:MailResentContent')
                        );
                    }
                });

            } else if (loginError === 'user_not_found' || loginError === 'wrong_credentials') {
                toastTitle = t('LOGIN:WrongCredentialsTitle');
                toastContent = t('LOGIN:WrongCredentialsContent');
                showToast = true;
            }

            if (!showToast) {

                return
            }

            Toast.show(toastTitle, toastContent)
        }

        evalLoginError()
    }, [ loginError ])

    return (
        <AuthLayout className="pl4-auth-layout--login">
            <main className="pl4-login-page">
                <section className="pl4-login-showcase" aria-hidden="true">
                    <div className="pl4-login-showcase__brand">
                        <img className="pl4-login-showcase__brand-icon" src="/svg-icons/logo-icon.svg" alt="" aria-hidden="true" />
                        <img className="pl4-login-showcase__brand-logo" src="/svg-icons/logo.svg" alt="PL4NNER" />
                    </div>

                    <div className="pl4-login-showcase__visual">
                        <div className="pl4-login-showcase__visual-head">
                            <span />
                            <span />
                            <span />
                        </div>
                        <div className="pl4-login-showcase__visual-grid">
                            <div className="pl4-login-showcase__metric">
                                <small>{t('LOGIN:ProjectProgress')}</small>
                                <strong>72%</strong>
                                <span><i style={{ width: '72%' }} /></span>
                            </div>
                            <div className="pl4-login-showcase__metric">
                                <small>{t('LOGIN:CompletedTasks')}</small>
                                <strong>38</strong>
                                <span><i style={{ width: '84%' }} /></span>
                            </div>
                            <div className="pl4-login-showcase__chart">
                                <span style={{ height: '32%' }} />
                                <span style={{ height: '48%' }} />
                                <span style={{ height: '62%' }} />
                                <span style={{ height: '54%' }} />
                                <span style={{ height: '78%' }} />
                                <span style={{ height: '92%' }} />
                            </div>
                        </div>
                    </div>
                </section>

                <section className="pl4-login-panel">
                    <div className="pl4-login-form">
                        <img className="pl4-login-form__logo" src="/images/logo.svg" alt="PL4NNER" />
                        <div className="pl4-login-form__heading">
                            <span>{t('LOGIN:Welcome')}</span>
                            <h1>{t('LOGIN:Login-title')}</h1>
                            <p>{t('LOGIN:Subtitle')}</p>
                        </div>

                        <form onSubmit={(event) => {
                            event.preventDefault()
                            handleSubmit()
                        }} noValidate>
                            <div className="pl4-login-field">
                                <label htmlFor="login-username">{t('LOGIN:Username')}</label>
                                <div className={`pl4-login-input${usernameError ? ' has-error' : ''}`}>
                                    <i className="far fa-user" aria-hidden="true" />
                                    <input
                                        id="login-username"
                                        type="text"
                                        placeholder={t('LOGIN:Username')}
                                        value={username}
                                        onChange={(event) => handleChange('username', event.target.value)}
                                        autoComplete="username"
                                        aria-invalid={Boolean(usernameError)}
                                        aria-describedby={usernameError ? 'login-username-error' : undefined}
                                        autoFocus
                                    />
                                </div>
                                {usernameError && (
                                    <span className="pl4-login-error" id="login-username-error">{usernameError}</span>
                                )}
                            </div>

                            <div className="pl4-login-field">
                                <div className="pl4-login-field__label-row">
                                    <label htmlFor="login-password">{t('LOGIN:Password')}</label>
                                    <Link to="/forgot-password">{t('LOGIN:ForgotPasswordButton')}</Link>
                                </div>
                                <div className={`pl4-login-input${passwordError ? ' has-error' : ''}`}>
                                    <i className="fas fa-lock" aria-hidden="true" />
                                    <input
                                        id="login-password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder={t('LOGIN:Password')}
                                        value={password}
                                        onChange={(event) => handleChange('password', event.target.value)}
                                        autoComplete="current-password"
                                        aria-invalid={Boolean(passwordError)}
                                        aria-describedby={passwordError ? 'login-password-error' : undefined}
                                    />
                                    <button
                                        type="button"
                                        className="pl4-login-input__toggle"
                                        onClick={() => setShowPassword((visible) => !visible)}
                                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                    >
                                        <i className={`far fa-eye${showPassword ? '-slash' : ''}`} aria-hidden="true" />
                                    </button>
                                </div>
                                {passwordError && (
                                    <span className="pl4-login-error" id="login-password-error">{passwordError}</span>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="pl4-btn pl4-btn-primary pl4-login-submit"
                                disabled={dispatching}
                            >
                                {dispatching && <i className="fas fa-circle-notch fa-spin" aria-hidden="true" />}
                                {!dispatching ? t('LOGIN:LoginButtonIdle') : t('LOGIN:LoginButtonLogingIn')}
                            </button>

                            <div className="pl4-login-register">
                                <span>{t('LOGIN:NoAccount')}</span>
                                <Link to="/register">{t('LOGIN:RegisterButton')}</Link>
                            </div>
                        </form>
                    </div>
                </section>
            </main>
        </AuthLayout>
    )
}

export default LoginPage
